import { ArrowUpRight, Check } from "lucide-react";
import { buildSitDownUrl, topicLabel } from "@/components/sitdown/constants";

export const PillarCard = ({ pillar, active, onSelect }) => {
  const { key, Icon, short, tagline } = pillar;
  return (
    <button
      type="button"
      data-testid={`sitdown-pillar-${key}`}
      aria-pressed={active}
      onClick={() => onSelect(key)}
      className={`group text-left border-t-2 pt-6 pb-4 transition-colors ${
        active ? "border-[var(--accent)]" : "border-slate-200 hover:border-slate-400"
      }`}
    >
      <Icon
        className={`w-6 h-6 mb-5 transition-colors ${
          active ? "text-[var(--accent)]" : "text-slate-400 group-hover:text-[#0A192F]"
        }`}
      />
      <div className="font-display text-2xl text-[#0A192F] leading-tight tracking-tight">
        {short}
      </div>
      <p className="mt-2 text-sm text-slate-500 leading-relaxed">{tagline}</p>
    </button>
  );
};

export const TopicList = ({ pillar, selected, onToggle }) => (
  <ul data-testid="sitdown-topic-list" className="grid grid-cols-1 gap-y-1">
    {pillar.topics.map((topic, i) => {
      const active = selected.includes(topic);
      const detail = topic.match(/\((.*)\)\s*$/)?.[1];
      return (
        <li key={topic}>
          <button
            type="button"
            data-testid={`sitdown-topic-${pillar.key}-${i}`}
            aria-pressed={active}
            onClick={() => onToggle(topic)}
            className={`w-full flex items-start gap-3 text-left py-3 border-b border-slate-200 text-sm transition-colors ${
              active ? "text-[#0A192F]" : "text-slate-600 hover:text-[#0A192F]"
            }`}
          >
            <span
              className={`mt-0.5 w-4 h-4 shrink-0 border flex items-center justify-center transition-colors ${
                active ? "border-[var(--accent)] bg-[var(--accent)] text-white" : "border-slate-300"
              }`}
            >
              {active && <Check className="w-3 h-3" />}
            </span>
            <span>
              <span className={active ? "font-medium" : ""}>{topicLabel(topic)}</span>
              {detail && <span className="block text-xs text-slate-500 mt-0.5">{detail}</span>}
            </span>
          </button>
        </li>
      );
    })}
  </ul>
);

export const SitDownSummary = ({ pillar, topics }) => {
  const ready = topics.length > 0;
  return (
    <div data-testid="sitdown-summary" className="border border-slate-200 bg-slate-50 p-6 md:p-8">
      <div className="text-xs tracking-tight text-slate-500 mb-4">Your sit-down request</div>
      <dl className="space-y-3 text-sm">
        <div className="flex gap-4">
          <dt className="w-16 shrink-0 text-slate-500">Category</dt>
          <dd data-testid="sitdown-summary-pillar" className="text-[#0A192F] font-medium">
            {pillar.title}
          </dd>
        </div>
        <div className="flex gap-4">
          <dt className="w-16 shrink-0 text-slate-500">Topics</dt>
          <dd data-testid="sitdown-summary-topic" className="text-[#0A192F]">
            {ready ? (
              <ul className="space-y-1">
                {topics.map((t) => <li key={t}>{topicLabel(t)}</li>)}
              </ul>
            ) : (
              <span className="text-slate-400">Pick one or more topics to continue</span>
            )}
          </dd>
        </div>
      </dl>
      <a
        href={ready ? buildSitDownUrl(pillar, topics) : undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={!ready}
        data-testid="sitdown-open-survey"
        className={`mt-8 inline-flex w-full items-center justify-between gap-3 px-6 py-4 text-sm font-medium tracking-tight transition-colors ${
          ready ? "bg-[var(--accent)] hover:bg-[#3b7bd0] text-white" : "bg-slate-200 text-slate-400 pointer-events-none"
        }`}
      >
        Open the sit-down survey
        <ArrowUpRight className="w-4 h-4" />
      </a>
      <p className="mt-4 text-xs text-slate-500 leading-relaxed">
        Opens the Google Form in a new tab with your category and topics already filled in — you
        just add your question and preferred format.
      </p>
    </div>
  );
};
